import { Link } from "react-router-dom";
import { MarketingLayout } from "../components/MarketingLayout";
import { useConfig } from "../lib/config";
import HeroStudio from "../components/visuals/HeroStudio";
import PipelineDiagram from "../components/visuals/PipelineDiagram";
import PlanCompare from "../components/visuals/PlanCompare";
import { Mic, Image as ImageIcon, Sparkles, Rocket, ArrowRight } from "lucide-react";

const STEPS = [
  { icon: Mic,       t: "Capture",    d: "Voice memo, notes, or a screen recording — transcribed and read for you." },
  { icon: ImageIcon, t: "Storyboard", d: "Screenshots become scenes with voiceover, captions, and on-screen text you edit as text." },
  { icon: Sparkles,  t: "Generate",   d: "Script, narration, captions, and launch copy, from the AI provider you choose." },
  { icon: Rocket,    t: "Ship",       d: "MP4, PDF manual, SRT captions, launch kit, and a shareable demo page." },
];

const AUDIENCES = [
  { t: "Founders launching",  d: "Ship a Product Hunt video the same week you ship the product." },
  { t: "SaaS product teams",  d: "Keep release walkthroughs current without booking an editor." },
  { t: "Agencies",            d: "White-label client demos in a workspace per account." },
];

export default function Landing() {
  const { waitlistMode, ready } = useConfig();

  // Until the flag resolves, keep the primary CTA's shape but leave it
  // inert — swapping "Start free" for "Join the waitlist" after paint
  // would move the page's most important control under the cursor.
  const primary = waitlistMode
    ? { to: "/waitlist", label: "Join the waitlist" }
    : { to: "/register", label: "Start free" };

  return (
    <MarketingLayout>

      {/* ================= HERO ================= */}
      <section
        className="max-w-wide mx-auto px-6 md:px-10 py-14 md:py-24 grid md:grid-cols-12 gap-10 lg:gap-14 items-center"
        data-testid="landing-hero"
      >
        <div className="md:col-span-6 lg:col-span-6">
          <p className="marquee-tag mb-6" data-testid="hero-tag">// AI DEMO PRODUCTION PLATFORM</p>

          <h1 className="font-display font-black type-display leading-none tracking-tighter mb-6">
            Screenshots.<br />Voice note.<br />
            <span className="text-brand">Launch video.</span>
          </h1>

          <p className="type-lead text-ink-muted max-w-prose mb-8">
            Turn rough product ideas, screenshots, and voice notes into polished
            SaaS demo videos — with storyboards, captions, launch copy, and
            export-ready renders.
          </p>

          {/* One primary action. Everything else is visibly subordinate. */}
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={primary.to}
              className="brutal-btn brutal-btn-lg"
              aria-disabled={!ready}
              onClick={(e) => { if (!ready) e.preventDefault(); }}
              data-testid="cta-primary"
            >
              {primary.label} <ArrowRight size={16} aria-hidden="true" />
            </Link>
            <Link to="/gallery" className="brutal-btn brutal-btn-secondary brutal-btn-lg" data-testid="cta-secondary">
              See examples
            </Link>
          </div>

          <p className="mt-4 font-mono text-xs text-ink-muted">
            {waitlistMode
              ? "Invite-only right now · no card, no account needed to join"
              : "3 projects free · no credit card"}{" "}
            ·{" "}
            <Link to="/pricing" className="text-ink-accent underline underline-offset-4 hover:no-underline">
              see pricing
            </Link>
          </p>

          <ul className="flex flex-wrap gap-x-5 gap-y-2 mt-8 pt-6 border-t border-rule-faint font-mono text-2xs uppercase tracking-widest text-ink-muted">
            {["14 templates", "Launch kit generator", "Bring your own AI keys", "Self-hostable"].map((f) => (
              <li key={f} className="flex items-center gap-1.5">
                <span aria-hidden="true" className="text-brand-ink font-bold">✓</span>
                {f}
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-6 lg:col-span-6">
          <HeroStudio />
        </div>
      </section>

      {/* ================= WORKFLOW ================= */}
      <section className="max-w-wide mx-auto px-6 md:px-10 py-16" data-testid="landing-features">
        <p className="marquee-tag mb-4">// WORKFLOW</p>
        <h2 className="font-display font-black type-heading-lg mb-10 tracking-tighter">
          From rough input<br />to launch-ready.
        </h2>

        <ol className="grid md:grid-cols-4 border border-rule">
          {STEPS.map(({ icon: Ic, t, d }, i) => (
            <li
              key={t}
              className={`p-7 border-rule border-b md:border-b-0 md:border-r last:border-0 ${
                i % 2 === 1 ? "bg-surface" : "bg-canvas"
              }`}
            >
              <Ic size={26} strokeWidth={2} aria-hidden="true" />
              <h3 className="font-display font-black type-subheading mt-5 tracking-tight">
                <span className="text-ink-subtle tabular-nums">{String(i + 1).padStart(2, "0")}</span>{" "}
                {t}
              </h3>
              <p className="text-sm mt-2.5 leading-snug text-ink-muted">{d}</p>
            </li>
          ))}
        </ol>

        <p className="mt-6">
          <Link to="/features" className="font-mono text-sm uppercase tracking-widest text-ink-accent underline underline-offset-4 hover:no-underline">
            All features →
          </Link>
        </p>
      </section>

      {/* ================= PIPELINE =================
          Replaces the previous testimonial block, which carried
          placeholder quotes explicitly labelled "not a real
          customer". Verifiable mechanics build more trust than
          invented praise. */}
      <section className="max-w-wide mx-auto px-6 md:px-10 py-16" data-testid="landing-pipeline">
        <p className="marquee-tag mb-4">// UNDER THE HOOD</p>
        <h2 className="font-display font-black type-heading-lg mb-3 tracking-tighter">
          No black box.
        </h2>
        <p className="type-lead text-ink-muted max-w-prose mb-10">
          Every run reports the stage it is on, live. These are the exact
          states your project moves through — the same ones you see on the
          dashboard.
        </p>
        <PipelineDiagram />
      </section>

      {/* ================= AUDIENCE ================= */}
      <section className="max-w-wide mx-auto px-6 md:px-10 py-16" data-testid="landing-social-proof">
        <p className="marquee-tag mb-4">// BUILT FOR</p>
        <ul className="grid md:grid-cols-3 gap-5">
          {AUDIENCES.map((a) => (
            <li key={a.t} className="brutal-card brutal-card-paper">
              <h3 className="font-display font-black type-subheading tracking-tight">{a.t}</h3>
              <p className="text-sm text-ink-muted mt-2 leading-snug">{a.d}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* ================= PLANS ================= */}
      <section className="max-w-wide mx-auto px-6 md:px-10 py-16" data-testid="landing-plans">
        <p className="marquee-tag mb-4">// LIMITS, PLAINLY</p>
        <h2 className="font-display font-black type-heading-lg mb-10 tracking-tighter">
          One number that matters.
        </h2>
        <PlanCompare />
        <p className="mt-6">
          <Link to="/pricing" className="font-mono text-sm uppercase tracking-widest text-ink-accent underline underline-offset-4 hover:no-underline">
            Full plan comparison →
          </Link>
        </p>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="max-w-wide mx-auto px-6 md:px-10 py-16 pb-24" data-testid="landing-cta">
        <div className="brutal-card brutal-card-ink on-ink p-10 md:p-16 shadow-brutal-lg">
          <p className="marquee-tag mb-6 !bg-canvas !text-ink">// START FREE</p>
          <h2 className="font-display font-black type-heading-lg tracking-tighter mb-6">
            Create polished walkthroughs<br />
            <span className="text-brand">without video editing.</span>
          </h2>
          <p className="text-white/75 mb-8 max-w-prose">
            {waitlistMode
              ? "We're onboarding gradually so every render gets the capacity it needs. Tell us what you're building and we'll email you an invite."
              : "3 demo projects free. No credit card."}{" "}
            Your product assets stay private — you control what gets exported.
          </p>
          <Link to={primary.to} className="brutal-btn brutal-btn-lg" data-testid="cta-final">
            {waitlistMode ? "Join the waitlist" : "Get started"} <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
